interface DomainPriority {
    domain: string;
    priority: 1 | 2 | 3;
}

class GoogleSheetsService {
    private readonly SHEET_ID = '1AEuZeLYGlYFUFHZg8E097EPcki1J7wPdN2nra08FMp8';
    private readonly PRIORITY_TABS = {
        1: '0',  // 1st Priority
        2: '961346831',  // 2nd Priority  
        3: '972786403'   // 3rd Priority
    };

    private domainCache: DomainPriority[] = [];
    private lastFetchTime: number = 0;
    private readonly CACHE_DURATION = 60 * 60 * 1000; // 1 hour

    async getDomainPriorities(): Promise<DomainPriority[]> {
        // Return cached data if still fresh
        if (this.domainCache.length > 0 && (Date.now() - this.lastFetchTime) < this.CACHE_DURATION) {
            return this.domainCache;
        }

        try {
            const priorities = await Promise.all([
                this.fetchDomainsFromTab(1),
                this.fetchDomainsFromTab(2), 
                this.fetchDomainsFromTab(3)
            ]);

            this.domainCache = priorities.flat();
            this.lastFetchTime = Date.now();
            
            console.log(`Fetched ${this.domainCache.length} domains from Google Sheets`);
            return this.domainCache;
        } catch (error) {
            console.error('Error fetching domains from Google Sheets:', error);
            return this.domainCache; // Return cached data on error
        }
    }

    private async fetchDomainsFromTab(priority: 1 | 2 | 3): Promise<DomainPriority[]> {
        const gid = this.PRIORITY_TABS[priority];
        const url = `https://docs.google.com/spreadsheets/d/${this.SHEET_ID}/export?format=csv&gid=${gid}`;
        
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to fetch priority ${priority} data: ${response.statusText}`);
        }

        const csvText = await response.text();
        const domains = this.parseCsvDomains(csvText);
        
        return domains.map(domain => ({
            domain: domain.toLowerCase(),
            priority
        }));
    }

    private parseCsvDomains(csvText: string): string[] {
        const lines = csvText.split('\n');
        const domains: string[] = [];

        // Skip header row, process data rows
        for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;

            // Split by comma and get column G (index 6)
            const columns = line.split(',');
            if (columns.length > 6) {
                const domain = columns[6].trim().replace(/"/g, ''); // Remove quotes
                if (domain && domain !== 'DOMAIN') {
                    domains.push(domain);
                }
            }
        }

        return domains;
    }

    getDomainPriority(serverName: string): 1 | 2 | 3 | null {
    const domain = this.extractDomainFromServerName(serverName);
    if (!domain) return null;
    
    // EXACT match only - no subdomain logic
    const exactMatch = this.domainCache.find(item => item.domain === domain);
    return exactMatch ? exactMatch.priority : null;
}

    private extractDomainFromServerName(serverName: string): string | null {
    // Extract domain from server name like "CDL Brevo 1 ANY PERSON domain.com"
    const parts = serverName.split(' ');
    const lastPart = parts[parts.length - 1];
    
    // Check if it looks like a domain (contains a dot)
    if (lastPart && lastPart.includes('.')) {
        return lastPart.toLowerCase().trim(); // Normalize to lowercase for consistent matching
    }
    
    return null;
}

    getPriorityEmoji(priority: number | null): string {
        switch (priority) {
            case 1: return '<span style="background-color: #28a745; color: white; border-radius: 50%; width: 16px; height: 16px; display: inline-flex; align-items: center; justify-content: center; font-size: 10px; font-weight: bold; margin-left: 5px;">1</span>';
            case 2: return '<span style="background-color: #ffc107; color: white; border-radius: 50%; width: 16px; height: 16px; display: inline-flex; align-items: center; justify-content: center; font-size: 10px; font-weight: bold; margin-left: 5px;">2</span>';
            case 3: return '<span style="background-color: #dc3545; color: white; border-radius: 50%; width: 16px; height: 16px; display: inline-flex; align-items: center; justify-content: center; font-size: 10px; font-weight: bold; margin-left: 5px;">3</span>';
            default: return '<span style="background-color: #6c757d; color: white; border-radius: 50%; width: 16px; height: 16px; display: inline-flex; align-items: center; justify-content: center; font-size: 10px; font-weight: bold; margin-left: 5px;">✕</span>';
        }
    }

    get cacheSize(): number {
    return this.domainCache.length;
    }
}

export const googleSheetsService = new GoogleSheetsService();
